import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TextField,
  InputAdornment,
  TableSortLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useTheme } from "../../../contexts/ThemeContext";
import { formatValueDecimal, findFund, findStrategy } from "../../../component/Methods";
import { lotNotionalUsd } from "../managerOverviewModel";
import { formatAumMillions } from "../managerOverviewDisplayUtils";
import { cycleColumnSort, compareLabelInsensitive, sortFundSmaTableEntries } from "./managerDetailDialogTableUtils";
import {
  fundLabel,
  smaLabel,
} from "./managerDetailDialogLabels";
import { ManagerOverviewStatCard } from "../ManagerOverviewStatCard";
import { FundSmaExpandableTable } from "./FundSmaExpandableTable";

const LOTS_PAGE_SIZE_OPTIONS = [25, 50, 100];
const LOTS_DEFAULT_PAGE_SIZE = 50;

/** Fixed tab body height so every tab (and the lots grid) uses the same vertical space. */
const TAB_BODY_MAX_HEIGHT = "min(720px, calc(96vh - 240px))";

/** Tab panel fills the capped tab body; children use overflow auto / flex grid scroll. */
function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return (
    <Box
      role="tabpanel"
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pt: 2,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   row: null | Record<string, unknown>;
 *   funds: unknown[];
 *   strategies: unknown[];
 * }} props
 */
export default function ManagerDetailDialog({
  open,
  onClose,
  row,
  funds,
  strategies,
}) {
  const { currentTheme: theme } = useTheme();
  const [tab, setTab] = useState(0);
  const [expandedFundSma, setExpandedFundSma] = useState(() => new Set());
  const [fundSort, setFundSort] = useState(() => ({ key: "name", dir: "asc" }));
  const [smaSort, setSmaSort] = useState(() => ({ key: "name", dir: "asc" }));
  const [lotsSearch, setLotsSearch] = useState("");
  const [lotsPaginationModel, setLotsPaginationModel] = useState(() => ({
    page: 0,
    pageSize: LOTS_DEFAULT_PAGE_SIZE,
  }));

  useEffect(() => {
    if (open) {
      setTab(0);
      setFundSort({ key: "name", dir: "asc" });
      setSmaSort({ key: "name", dir: "asc" });
      setLotsSearch("");
      setLotsPaginationModel({ page: 0, pageSize: LOTS_DEFAULT_PAGE_SIZE });
    }
  }, [open, row?.name, row?.managerId]);

  useEffect(() => {
    setExpandedFundSma(new Set());
  }, [open, row?.managerId, row?.name]);

  const headSx = useMemo(
    () => ({
      fontWeight: 600,
      fontSize: 12,
      backgroundColor: theme.palette.background.tableHeader,
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.divider}`,
    }),
    [theme]
  );

  const cellSx = useMemo(
    () => ({
      fontSize: 13,
      borderColor: theme.palette.divider,
      color: theme.palette.text.primary,
    }),
    [theme]
  );

  /** Popup payload lives under `details`; fall back to flat row for older shapes. */
  const d = row?.details ?? row;

  const lotsForManager = useMemo(() => d?.lotsForManager ?? [], [d?.lotsForManager]);

  const fundEntries = useMemo(() => d?.fundEntries ?? [], [d?.fundEntries]);
  const smaEntries = useMemo(() => d?.smaEntries ?? [], [d?.smaEntries]);
  const fundSmaMetrics = useMemo(
    () => d?.fundSmaMetrics ?? { funds: new Map(), smas: new Map() },
    [d?.fundSmaMetrics]
  );
  const strategyChildrenMaps = useMemo(
    () => d?.strategyChildrenMaps ?? { funds: new Map(), smas: new Map() },
    [d?.strategyChildrenMaps]
  );

  const strategiesSorted = useMemo(() => {
    const list = [...(d?.strategiesForManager ?? [])];
    list.sort((a, b) => compareLabelInsensitive(a?.name, b?.name));
    return list;
  }, [d?.strategiesForManager]);

  const defaultFundSmaMetrics = useMemo(
    () => ({ aum: 0, week: 0, month: 0, quarter: 0, year: 0, days365: 0 }),
    []
  );

  const sortedFundEntries = useMemo(
    () =>
      sortFundSmaTableEntries({
        entries: fundEntries,
        metricsMap: fundSmaMetrics.funds,
        sort: fundSort,
        defaultMetrics: defaultFundSmaMetrics,
        nameCmp: (ea, eb) =>
          compareLabelInsensitive(fundLabel(ea.id, ea.data, funds), fundLabel(eb.id, eb.data, funds)),
      }),
    [fundEntries, fundSmaMetrics.funds, fundSort, funds, defaultFundSmaMetrics]
  );

  const sortedSmaEntries = useMemo(
    () =>
      sortFundSmaTableEntries({
        entries: smaEntries,
        metricsMap: fundSmaMetrics.smas,
        sort: smaSort,
        defaultMetrics: defaultFundSmaMetrics,
        nameCmp: (ea, eb) => compareLabelInsensitive(smaLabel(ea.id, ea.data), smaLabel(eb.id, eb.data)),
      }),
    [smaEntries, fundSmaMetrics.smas, smaSort, defaultFundSmaMetrics]
  );

  const hoverChildBg = useMemo(
    () =>
      theme.palette.mode === "light"
        ? alpha(theme.palette.action.hover, 0.45)
        : alpha(theme.palette.action.hover, 0.12),
    [theme]
  );

  const changeTone = useCallback((v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return theme.palette.text.secondary;
    if (n > 0) return theme.palette.success.main;
    return theme.palette.error.main;
  }, [theme]);

  const lotsSearchTrim = useMemo(() => lotsSearch.trim().toLowerCase(), [lotsSearch]);

  const lotsPrepared = useMemo(() => {
    return lotsForManager.map((lot, lotIndex) => {
      const fid = lot?.fund != null ? String(lot.fund) : "";
      const sym = lot?.ticker?.symbol || "";
      const stratName = findStrategy(lot?.strategy, strategies) || "";
      const sc = Number(lot?.shareCount);
      const side = sc < 0 ? "Short" : sc > 0 ? "Long" : "Flat";
      const price = Math.abs(Number(lot?.sharePrice) || 0);
      const notional = lotNotionalUsd(lot);
      const fundName = findFund(fid, funds) || "";
      const hay = [fundName, stratName, sym, side, fid, String(lot?.lotId ?? ""), String(lot?.strategy ?? "")]
        .join(" ")
        .toLowerCase();
      return { lot, lotIndex, fundName, stratName, sym, side, shares: sc, price, notional, hay };
    });
  }, [lotsForManager, funds, strategies]);

  const lotsGridRows = useMemo(() => {
    const terms = lotsSearchTrim.split(/\s+/).filter(Boolean);
    const base =
      terms.length === 0 ? lotsPrepared : lotsPrepared.filter((p) => terms.every((t) => p.hay.includes(t)));
    return base.map((p) => {
      const { lotIndex } = p;
      return {
        id: `mgr-lot-${lotIndex}`,
        fundName: p.fundName || "—",
        stratName: p.stratName || "—",
        sym: p.sym || "—",
        side: p.side,
        shares: p.shares,
        price: p.price,
        notional: p.notional,
      };
    });
  }, [lotsPrepared, lotsSearchTrim]);

  const lotsFilteredCount = lotsGridRows.length;

  useEffect(() => {
    setLotsPaginationModel((m) => ({ ...m, page: 0 }));
  }, [lotsSearchTrim]);

  useEffect(() => {
    setLotsPaginationModel((m) => {
      const lastPage = Math.max(0, Math.ceil(lotsFilteredCount / m.pageSize) - 1);
      return m.page > lastPage ? { ...m, page: lastPage } : m;
    });
  }, [lotsFilteredCount, lotsPaginationModel.pageSize]);

  const lotsColumns = useMemo(
    () => [
      {
        field: "fundName",
        headerName: "Fund / SMA",
        flex: 1.1,
        minWidth: 140,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: theme.palette.text.primary }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "stratName",
        headerName: "Strategy",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: 13, color: theme.palette.text.primary }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "sym",
        headerName: "Ticker",
        width: 100,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: 13, color: theme.palette.text.primary }}>
            {params.value}
          </Typography>
        ),
      },
      { field: "side", headerName: "Side", width: 88 },
      {
        field: "shares",
        headerName: "Shares",
        type: "number",
        width: 112,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}>
            {Number(params.value)?.toLocaleString?.() ?? params.value}
          </Typography>
        ),
      },
      {
        field: "price",
        headerName: "Price",
        type: "number",
        width: 110,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}>
            ${formatValueDecimal(Number(params.value) || 0)}
          </Typography>
        ),
      },
      {
        field: "notional",
        headerName: "Notional",
        type: "number",
        flex: 0.85,
        minWidth: 112,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}>
            ${formatValueDecimal(Number(params.value) || 0)}
          </Typography>
        ),
      },
    ],
    [theme]
  );

  const lotsDataGridSx = useMemo(() => {
    const tableBg =
      theme.palette.mode === "dark"
        ? theme.palette.grey[100]
        : theme.palette.background.paper;
    return {
      border: "none",
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      backgroundColor: tableBg,
      "& .MuiDataGrid-main": {
        backgroundColor: "inherit",
      },
      "& .MuiDataGrid-columnHeaders": {
        flexShrink: 0,
        backgroundColor: theme.palette.grey[100],
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: 12,
        fontWeight: 600,
      },
      "& .MuiDataGrid-columnHeader": {
        color: theme.palette.text.primary,
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 600,
      },
      /* Default grid uses row-reverse on right-aligned headers (icon before label). Show name then sort arrow. */
      "& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderDraggableContainer": {
        flexDirection: "row",
      },
      "& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer": {
        flexDirection: "row",
        justifyContent: "flex-end",
      },
      "& .MuiDataGrid-virtualScroller": {
        flex: "1 1 auto",
        minHeight: 0,
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
      },
      "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
      },
      "& .MuiDataGrid-footerContainer": {
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[100],
      },
      "& .MuiDataGrid-cell": {
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        fontSize: 13,
      },
      "& .MuiDataGrid-row": {
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
      },
      "& .MuiDataGrid-row:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    };
  }, [theme]);

  if (!row) return null;

  const {
    name,
    aum,
    fundCount,
    smaCount,
    strategyCount,
  } = row;

  const totalLots = lotsForManager.length;

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  const toggleExpand = (kind, key) => {
    const composite = `${kind}:${key}`;
    setExpandedFundSma((prev) => {
      const next = new Set(prev);
      if (next.has(composite)) next.delete(composite);
      else next.add(composite);
      return next;
    });
  };

  const makeFundSmaHeadRow = (sort, setSort) => {
    const req = (key) => () => setSort((s) => cycleColumnSort(key, s));
    const sortLabelSx = (align, colKey) => ({
      whiteSpace: "nowrap",
      width: "100%",
      flexDirection: "row !important",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      "& .MuiTableSortLabel-icon": {
        marginLeft: "6px",
        marginRight: 0,
        opacity: sort.key === colKey ? 1 : 0.35,
      },
    });
    const sortable = (key, label, align = "left", extraSx = {}) => (
      <TableCell
        align={align}
        sortDirection={sort.key === key ? sort.dir : false}
        sx={{
          ...headSx,
          whiteSpace: "nowrap",
          lineHeight: 1.2,
          py: 1,
          ...extraSx,
        }}
      >
        <TableSortLabel
          active={sort.key === key}
          direction={sort.key === key ? sort.dir : "asc"}
          onClick={req(key)}
          title={
            key === "week"
              ? "Ch(week)"
              : key === "month"
                ? "Ch(month)"
                : key === "quarter"
                  ? "Ch(quarter)"
                  : key === "year"
                    ? "Ch(year)"
                    : key === "days365"
                      ? "Ch(365)"
                      : undefined
          }
          sx={sortLabelSx(align, key)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
    return (
      <TableRow>
        <TableCell sx={{ ...headSx, width: 48, maxWidth: 48, whiteSpace: "nowrap", py: 1 }} />
        {sortable("name", "Name", "left")}
        <TableCell
          sx={{
            ...headSx,
            whiteSpace: "nowrap",
            lineHeight: 1.2,
            py: 1,
          }}
        >
          Type
        </TableCell>
        {sortable("aum", "AUM (M)", "right")}
        {sortable("week", "Ch(week)", "right")}
        {sortable("month", "Ch(month)", "right")}
        {sortable("quarter", "Ch(quarter)", "right")}
        {sortable("year", "Ch(year)", "right")}
        {sortable("days365", "Ch(365)", "right")}
      </TableRow>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      disableRestoreFocus
      closeAfterTransition={false}
      PaperProps={{
        sx: {
          maxHeight: "min(1040px, 96vh)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          flexShrink: 0,
          pt: 3,
          pb: 2.5,
          pl: 3,
          pr: 7,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ pr: 1, maxWidth: "calc(100% - 40px)" }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
            {name}
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1, lineHeight: 1.45 }}>
            Book detail — positions, funds, SMAs, and strategies
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 12, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: theme.palette.background.default,
          pt: 4,
          pb: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: "grid",
            gap: 2,
            mt: 1,
            mb: 3,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
          }}
        >
          <Box sx={{ display: "flex", minWidth: 0 }}>
            <ManagerOverviewStatCard
              compact
              theme={theme}
              title="AUM (Accounting)"
              value={formatAumMillions(aum)}
              icon={AttachMoneyOutlinedIcon}
            />
          </Box>
          <Box sx={{ display: "flex", minWidth: 0 }}>
            <ManagerOverviewStatCard compact theme={theme} title="Funds" value={fundCount} icon={PieChartOutlineIcon} />
          </Box>
          <Box sx={{ display: "flex", minWidth: 0 }}>
            <ManagerOverviewStatCard compact theme={theme} title="SMAs" value={smaCount} icon={LayersOutlinedIcon} />
          </Box>
          <Box sx={{ display: "flex", minWidth: 0 }}>
            <ManagerOverviewStatCard
              compact
              theme={theme}
              title="Strategies"
              value={strategyCount}
              icon={AccountTreeOutlinedIcon}
            />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flexShrink: 0, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Funds & SMAs" />
            <Tab label="Strategies" />
            <Tab label="Lots" />
          </Tabs>

          <Box
            sx={{
              height: TAB_BODY_MAX_HEIGHT,
              maxHeight: TAB_BODY_MAX_HEIGHT,
              width: "100%",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
        <TabPanel value={tab} index={2}>
          {totalLots === 0 ? (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography color="text.secondary">No lots matched this manager&apos;s funds / SMAs.</Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1, overflow: "hidden" }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search lots"
                value={lotsSearch}
                onChange={(e) => setLotsSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {lotsSearchTrim
                  ? lotsFilteredCount === 0
                    ? `No lots match "${lotsSearch.trim()}".`
                    : (() => {
                        const { page, pageSize } = lotsPaginationModel;
                        const start = page * pageSize + 1;
                        const end = Math.min((page + 1) * pageSize, lotsFilteredCount);
                        return `${lotsFilteredCount.toLocaleString()} match${lotsFilteredCount === 1 ? "" : "es"} · showing rows ${start.toLocaleString()}–${end.toLocaleString()} (${totalLots.toLocaleString()} total lots on book).`;
                      })()
                  : `${totalLots.toLocaleString()} lots  ${lotsFilteredCount > 0 ? (() => {
                      const { page, pageSize } = lotsPaginationModel;
                      const start = page * pageSize + 1;
                      const end = Math.min((page + 1) * pageSize, lotsFilteredCount);
                      return ` · rows ${start.toLocaleString()}–${end.toLocaleString()} on this page`;
                    })() : ""}`}
              </Typography>
              <Box
                sx={{
                  flex: "1 1 0%",
                  minHeight: 300,
                  width: "100%",
                  position: "relative",
                }}
              >
                <DataGrid
                  rows={lotsGridRows}
                  columns={lotsColumns}
                  density="compact"
                  disableRowSelectionOnClick
                  disableColumnMenu
                  pagination
                  paginationModel={lotsPaginationModel}
                  onPaginationModelChange={(model) =>
                    setLotsPaginationModel({
                      page: model.page,
                      pageSize: Math.min(model.pageSize, 100),
                    })
                  }
                  pageSizeOptions={LOTS_PAGE_SIZE_OPTIONS}
                  sortingOrder={["asc", "desc"]}
                  initialState={{
                    sorting: { sortModel: [{ field: "fundName", sort: "asc" }] },
                  }}
                  sx={{
                    ...lotsDataGridSx,
                    position: "absolute",
                    inset: 0,
                    minHeight: 0,
                  }}
                />
              </Box>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tab} index={0}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Funds
            </Typography>
            {fundEntries.length === 0 ? (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No funds on this manager record.
              </Typography>
            ) : (
              <FundSmaExpandableTable
                kind="fund"
                sortedEntries={sortedFundEntries}
                metricsByKey={fundSmaMetrics.funds}
                strategiesByKey={strategyChildrenMaps.funds}
                expandedSet={expandedFundSma}
                onToggleExpand={toggleExpand}
                funds={funds}
                cellSx={cellSx}
                hoverChildBg={hoverChildBg}
                changeTone={changeTone}
                tableHead={makeFundSmaHeadRow(fundSort, setFundSort)}
                tableContainerSx={{ mb: 3 }}
              />
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              SMAs
            </Typography>
            {smaEntries.length === 0 ? (
              <Typography color="text.secondary">No SMAs on this manager record.</Typography>
            ) : (
              <FundSmaExpandableTable
                kind="sma"
                sortedEntries={sortedSmaEntries}
                metricsByKey={fundSmaMetrics.smas}
                strategiesByKey={strategyChildrenMaps.smas}
                expandedSet={expandedFundSma}
                onToggleExpand={toggleExpand}
                funds={funds}
                cellSx={cellSx}
                hoverChildBg={hoverChildBg}
                changeTone={changeTone}
                tableHead={makeFundSmaHeadRow(smaSort, setSmaSort)}
              />
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {strategiesSorted.length === 0 ? (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography color="text.secondary">No strategies with owner matching this manager.</Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  borderRadius: 0,
                  overflow: "auto",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Strategy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {strategiesSorted.map((s) => (
                      <TableRow key={s?.strategyId || s?.name}>
                        <TableCell sx={cellSx}>
                          <Typography fontWeight={600}>{s?.name ?? "—"}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </TabPanel>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
